var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createElement, formatUnit, updateCSSText, detach, isNullOrUndefined as isNOU } from '@syncfusion/ej2-base';
import { BlockType } from '../../../models/enums';
import { getBlockModelById, decoupleReference } from '../../../common/utils/index';
import { events } from '../../../common/constant';
// Constants
const MIN_IMAGE_WIDTH = 40;
const MIN_IMAGE_HEIGHT = 40;
const RESIZE_HANDLE_SIZE = 8;
export class ImageRenderer {
    constructor(manager) {
        this.isResizing = false;
        this.handleImageResize = (e) => {
            const allowedTarget = this.parent.rootEditorElement;
            if (!allowedTarget.contains(e.target) && (!this.isResizing)) {
                return;
            }
            const dx = e.clientX - this.startPosition.x;
            const dy = e.clientY - this.startPosition.y;
            const position = this.currentResizeHandle.className.split('e-resize-')[1];
            const { newWidth, newHeight } = this.calculateNewDimensions(dx, dy, position);
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = requestAnimationFrame(() => {
                if (this.currentImage && this.currentImage.parentElement) {
                    updateCSSText(this.currentImage, `width: ${formatUnit(newWidth)}; height: ${formatUnit(newHeight)};`);
                    updateCSSText(this.currentImage.parentElement, `width: ${formatUnit(newWidth)}; height: ${formatUnit(newHeight)};`);
                }
            });
        };
        this.stopImageResize = () => {
            if (!this.isResizing) {
                return;
            }
            document.removeEventListener('mousemove', this.handleImageResize);
            document.removeEventListener('mouseup', this.stopImageResize);
            if (this.resizeOverlay && this.resizeOverlay.parentNode) {
                document.body.removeChild(this.resizeOverlay);
            }
            this.isResizing = false;
            this.currentResizeHandle = null;
            this.currentImage = null;
        };
        this.parent = manager;
        this.uploadPopupObj = null;
        this.uploadPopupElement = null;
        this.currentPlaceholder = null;
        this.isUploadPopupOpen = false;
        this.addEventListeners();
        // Create upload popup during initialization to ensure uploader is ready
    }
    addEventListeners() {
        this.parent.observer.on(events.documentClick, this.handleDocumentClick, this);
        this.parent.observer.on('modulesInitialized', this.createUploadPopup, this);
        this.parent.observer.on(events.destroy, this.destroy, this);
    }
    removeEventListeners() {
        this.parent.observer.off(events.documentClick, this.handleDocumentClick);
        this.parent.observer.off('modulesInitialized', this.createUploadPopup);
        this.parent.observer.off(events.destroy, this.destroy);
    }
    /**
     * Renders a image block
     *
     * @param {BlockModel} block - The block model containing data.
     * @returns {HTMLElement} - The rendered image block element.
     * @hidden
     */
    renderImage(block) {
        const settings = block.properties;
        const isCallFromBlazor = isNOU(this.parent.rootEditorElement.ej2_instances);
        // Check if image has source - if not, render placeholder
        if (!isCallFromBlazor && (!settings.src || settings.src === '')) {
            const placeholder = this.renderPlaceholder(block.id);
            // Only auto-open popup if NOT during undo/redo and NOT during initial rendering
            const isUndoRedo = this.parent.undoRedoAction.isUndoing || this.parent.undoRedoAction.isRedoing;
            if (!isUndoRedo && (this.parent.blockRenderer && !this.parent.blockRenderer.isEntireBlocksRendering)) {
                // Open upload popup after a short delay to ensure placeholder is appended in dom
                setTimeout(() => {
                    this.toggleUploadPopup(isNOU(this.parent.currentFocusedBlock), placeholder);
                }, 100);
            }
            return placeholder;
        }
        // Render normal image if src exists
        const { container, img } = this.createImageContainer(block);
        this.configureImageElement(img, block);
        container.appendChild(img);
        return container;
    }
    renderPlaceholder(blockId) {
        const placeholder = createElement('div', {
            id: `${this.parent.rootEditorElement.id}_image-placeholder-${blockId}`,
            className: 'e-image-placeholder',
            attrs: {
                'role': 'button',
                'aria-label': this.parent.localeJson['imgPlaceholderAriaLabel'],
                'tabindex': '0',
                'data-block-id': blockId,
                contenteditable: 'false'
            }
        });
        // Create placeholder icon
        const iconContainer = createElement('div', {
            className: 'e-placeholder-icon-container'
        });
        const icon = createElement('span', {
            className: 'e-icons e-block-image-icon'
        });
        iconContainer.appendChild(icon);
        // Create placeholder text
        const textElement = createElement('div', {
            className: 'e-placeholder-text',
            innerHTML: this.parent.localeJson['imagePlaceholder']
        });
        placeholder.appendChild(iconContainer);
        placeholder.appendChild(textElement);
        // Bind click event
        placeholder.addEventListener('click', this.handlePlaceholderClick.bind(this));
        // Bind keyboard events
        placeholder.addEventListener('keydown', this.handlePlaceholderKeydown.bind(this));
        return placeholder;
    }
    handlePlaceholderClick(event) {
        event.preventDefault();
        const target = event.currentTarget;
        if (this.currentPlaceholder !== target) {
            this.toggleUploadPopup(false, target);
        }
        else {
            this.toggleUploadPopup(true);
            this.currentPlaceholder = null;
        }
    }
    handlePlaceholderKeydown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            const target = event.currentTarget;
            this.toggleUploadPopup(false, target);
        }
    }
    toggleUploadPopup(shouldHide, placeholder) {
        if (shouldHide) {
            if (this.uploadPopupObj && this.isUploadPopupOpen) {
                this.uploadPopupObj.hide();
                this.isUploadPopupOpen = false;
                this.currentPlaceholder = null;
            }
        }
        else {
            if (placeholder) {
                this.currentPlaceholder = placeholder;
            }
            // Show popup
            if (this.uploadPopupObj && this.currentPlaceholder) {
                this.uploadPopupObj.relateTo = this.currentPlaceholder;
                this.parent.popupRenderer.adjustPopupPositionRelativeToTarget(this.currentPlaceholder, this.uploadPopupObj);
                this.parent.observer.notify('clearUploaderObj');
                this.uploadPopupObj.show();
                this.isUploadPopupOpen = true;
            }
        }
    }
    createUploadPopup() {
        // Create popup container
        this.uploadPopupElement = createElement('div', {
            id: `${this.parent.rootEditorElement.id}_image-upload-popup`,
            className: 'e-image-upload-popup e-popup-container'
        });
        // Create popup content container
        const contentContainer = createElement('div', {
            id: `${this.parent.rootEditorElement.id}_image-tab-container`,
            className: 'e-popup-content'
        });
        this.uploadPopupElement.appendChild(contentContainer);
        this.parent.rootEditorElement.appendChild(this.uploadPopupElement);
        // Notify BlockEditor to render the tab component in this container
        this.parent.observer.notify('renderImageUploader', { container: contentContainer });
        // Use PopupRenderer to create the popup
        const args = {
            element: this.uploadPopupElement,
            content: contentContainer,
            width: '400px',
            height: 'auto'
        };
        this.uploadPopupObj = this.parent.popupRenderer.renderPopup(args);
        // Bind popup lifecycle events
        this.uploadPopupObj.open = this.handlePopupOpen.bind(this);
        this.uploadPopupObj.close = this.handlePopupClose.bind(this);
        // Bind escape key to close popup
        this.uploadPopupElement.addEventListener('keydown', this.handlePopupKeydown.bind(this));
        // Subscribe to image selection and upload events
        this.subscribeToImageEvents();
    }
    subscribeToImageEvents() {
        // Listen for file selected event to show Base64 preview
        this.parent.observer.on('fileSelected', this.handleFileSelected, this);
        // Listen for upload success to replace with server URL
        this.parent.observer.on('fileUploadSuccess', this.handleUploadSuccess, this);
        // Listen for embed image event
        this.parent.observer.on('imageEmbedded', this.handleImageEmbedded, this);
    }
    unsubscribeFromImageEvents() {
        this.parent.observer.off('fileSelected', this.handleFileSelected);
        this.parent.observer.off('fileUploadSuccess', this.handleUploadSuccess);
        this.parent.observer.off('imageEmbedded', this.handleImageEmbedded);
    }
    handleFileSelected(args) {
        if (!this.currentPlaceholder || !args.previewUrl) {
            return;
        }
        // Get the block ID from placeholder
        const blockId = this.currentPlaceholder.getAttribute('data-block-id') || '';
        // Replace placeholder with image preview
        this.replaceWithImage(this.currentPlaceholder, args.previewUrl, args.file.name, blockId);
        // Close the popup
        this.toggleUploadPopup(true);
    }
    handleUploadSuccess(args) {
        const blockElement = this.parent.rootEditorElement.querySelector(`[data-block-id="${args.blockId}"]`);
        if (!blockElement) {
            return;
        }
        // Find the image element and update its src
        const imgElement = blockElement.querySelector('img.e-image-block');
        if (imgElement) {
            imgElement.src = args.fileUrl;
            // Update the block model
            const block = getBlockModelById(args.blockId, this.parent.getEditorBlocks());
            if (block) {
                block.properties.src = args.fileUrl;
            }
        }
    }
    handleImageEmbedded(args) {
        if (!this.currentPlaceholder || !args.url) {
            return;
        }
        // Get the block ID from placeholder
        const blockId = this.currentPlaceholder.getAttribute('data-block-id') || '';
        // Replace placeholder with image
        this.replaceWithImage(this.currentPlaceholder, args.url, 'Embedded image', blockId);
        // Close the popup
        this.toggleUploadPopup(true);
    }
    // Replaces placeholder element with actual image element using BlockCommand API.
    replaceWithImage(placeholder, imageSrc, altText, blockId) {
        const blockElement = placeholder.parentElement;
        if (!blockElement) {
            return;
        }
        const oldBlock = getBlockModelById(blockId, this.parent.getEditorBlocks());
        if (!oldBlock) {
            return;
        }
        // old block model (src empty)
        const oldBlockClone = decoupleReference(oldBlock);
        // Update the block with the new src
        const updatedProps = Object.assign({}, oldBlock.properties, { src: imageSrc, altText: altText });
        // Update the block model
        oldBlock.properties = updatedProps;
        const newBlock = this.parent.blockService.updateBlock(blockId, oldBlock);
        this.parent.stateManager.updateManagerBlocks();
        const newBlockClone = decoupleReference(newBlock);
        const newBlockElement = this.parent.blockRenderer.createBlockElement(newBlock);
        // Replace the old placeholder block element with the new image block element
        blockElement.parentElement.insertBefore(newBlockElement, blockElement);
        detach(blockElement);
        this.parent.setFocusToBlock(newBlockElement);
        this.parent.undoRedoAction.trackImageInsertionForUndoRedo(blockId, oldBlockClone, newBlockClone);
        this.parent.eventService.addChange({
            action: 'Update',
            data: {
                block: newBlockClone,
                prevBlock: oldBlockClone
            }
        });
        this.parent.observer.notify('triggerBlockChange', this.parent.eventService.getChanges());
        // Clear current placeholder reference
        this.currentPlaceholder = null;
    }
    handlePopupOpen() {
        this.isUploadPopupOpen = true;
        // Notify BlockEditor's tab renderer via event
        this.parent.observer.notify('imagePopupOpen', {});
    }
    handlePopupClose() {
        this.isUploadPopupOpen = false;
        // Notify BlockEditor's tab renderer via event
        this.parent.observer.notify('imagePopupClose', {});
    }
    handlePopupKeydown(event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            this.toggleUploadPopup(true);
        }
    }
    /**
     * Gets the current placeholder element.
     *
     * @returns {HTMLElement | null} The current image placeholder element
     * @hidden
     */
    getCurrentPlaceholder() {
        return this.currentPlaceholder;
    }
    createImageContainer(block) {
        const container = createElement('div', {
            className: 'e-image-container',
            attrs: { 'data-block-id': block.id, contenteditable: 'false' }
        });
        updateCSSText(container, 'position: relative;');
        const img = createElement('img', {
            className: 'e-image-block',
            attrs: { 'alt': block.properties.altText || '', 'role': 'img', 'data-block-id': block.id }
        });
        return { container, img };
    }
    configureImageElement(img, block) {
        const settings = block.properties;
        const globalImgSettings = this.parent.imageBlockSettings;
        updateCSSText(img, `width: ${formatUnit(settings.width ? settings.width : globalImgSettings.width)};`);
        updateCSSText(img, `height: ${formatUnit(settings.height ? settings.height : globalImgSettings.height)};`);
        const isUndoRedo = this.parent.undoRedoAction.isUndoing || this.parent.undoRedoAction.isRedoing;
        if (settings.src) {
            img.src = settings.src;
        }
        else {
            this.handleImageUpload(img, settings);
        }
        if (settings.altText) {
            img.setAttribute('aria-label', settings.altText);
        }
        img.addEventListener('load', () => {
            updateCSSText(img, 'display: block;');
            const minWidth = globalImgSettings.minWidth ? `min-width: ${formatUnit(globalImgSettings.minWidth)};` : '';
            const minHeight = globalImgSettings.minHeight ? `min-height: ${formatUnit(globalImgSettings.minHeight)};` : '';
            const maxWidth = globalImgSettings.maxWidth ? `max-width: ${formatUnit(globalImgSettings.maxWidth)};` : '';
            const maxHeight = globalImgSettings.maxHeight ? `max-height: ${formatUnit(globalImgSettings.maxHeight)};` : '';
            updateCSSText(img, minWidth + minHeight + maxWidth + maxHeight);
            this.aspectRatio = img.naturalWidth / img.naturalHeight;
            // Should notify only during image inserted via uploader and paste action.
            if ((this.parent.blockRenderer && !this.parent.blockRenderer.isEntireBlocksRendering) && !isUndoRedo) {
                this.parent.observer.notify('imageInserted', { blockId: block.id, imgElement: img });
            }
            if (this.parent.imageBlockSettings.enableResize) {
                this.addResizeHandles(img.parentElement, img);
            }
        });
    }
    /**
     * Handles image upload
     *
     * @param {HTMLImageElement} img - The image element whose source will be updated.
     * @param {IImageBlockSettings} settings - Image configuration including allowedTypes and saveFormat.
     * @returns {void}
     * @hidden
     */
    handleImageUpload(img, settings) {
        const fileInput = createElement('input', {
            attrs: {
                type: 'file',
                accept: this.parent.imageBlockSettings.allowedTypes.join(',')
            }
        });
        updateCSSText(fileInput, 'display: none;');
        document.body.appendChild(fileInput);
        const handleFileChange = () => __awaiter(this, void 0, void 0, function* () {
            const file = fileInput.files[0];
            if (!file) {
                document.body.removeChild(fileInput);
                return;
            }
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            if (this.parent.imageBlockSettings.allowedTypes.indexOf(fileExtension) === -1) {
                document.body.removeChild(fileInput);
                return;
            }
            const src = yield this.getImageSrcFromFile(file, this.parent.imageBlockSettings.saveFormat);
            img.src = settings.src = src;
            fileInput.removeEventListener('change', handleFileChange);
            if (fileInput.parentNode) {
                fileInput.parentNode.removeChild(fileInput);
            }
        });
        fileInput.addEventListener('change', handleFileChange);
        fileInput.click();
    }
    handleDocumentClick(mouseEvent) {
        const target = mouseEvent.target;
        // Handle upload popup visibility logic
        if (this.uploadPopupObj && this.isUploadPopupOpen) {
            const isInsidePopup = this.uploadPopupElement && this.uploadPopupElement.contains(target);
            const isOnPlaceholder = this.currentPlaceholder && this.currentPlaceholder.contains(target);
            // Close popup if clicking outside both popup and placeholder
            if (!isInsidePopup && !isOnPlaceholder) {
                this.toggleUploadPopup(true);
            }
        }
        // Handle image resize handles visibility
        const isImageClick = target.matches('img') || target.getAttribute('data-block-type') === BlockType.Image;
        const images = this.parent.rootEditorElement.querySelectorAll('img');
        for (const image of Array.from(images)) {
            const isTargetImage = image === target || target.contains(image);
            const resizeHandles = image.parentElement.querySelectorAll('.e-image-rsz-handle');
            for (const handle of Array.from(resizeHandles)) {
                updateCSSText(handle, `display: ${isImageClick && isTargetImage ? 'block' : 'none'};`);
            }
            image.classList.toggle('e-image-focus', isImageClick && isTargetImage);
        }
    }
    /**
     * Handles the paste event for images.
     *
     * @param {File | Blob} file - The file or blob to be pasted.
     * @returns {Promise<void>} - A promise that resolves when the image is pasted.
     * @hidden
     */
    handleFilePaste(file) {
        return __awaiter(this, void 0, void 0, function* () {
            const blockElement = this.parent.currentFocusedBlock;
            const block = getBlockModelById(blockElement.id, this.parent.getEditorBlocks());
            const saveFormat = this.parent.imageBlockSettings.saveFormat;
            const src = yield this.getImageSrcFromFile(file, (saveFormat || 'Base64'));
            const fileName = (file instanceof File) ? file.name : `image-${Date.now()}`;
            let transformedParagraph = blockElement;
            if (((blockElement.textContent.length > 0))) {
                const addedBlock = this.parent.blockCommand.addBlock({
                    targetBlock: blockElement,
                    blockType: BlockType.Paragraph
                });
                transformedParagraph = this.parent.getBlockElementById(addedBlock.id);
            }
            this.parent.blockCommand.transformBlock({
                block: getBlockModelById(transformedParagraph.id, this.parent.getEditorBlocks()),
                blockElement: transformedParagraph,
                newBlockType: BlockType.Image,
                props: { src: src, altText: fileName }
            });
        });
    }
    getImageSrcFromFile(file, saveFormat) {
        const format = saveFormat.toLowerCase();
        if (format === 'base64') {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (event) => { resolve(event.target.result); };
                reader.onerror = () => { reject(new Error('Failed to read image as base64.')); };
                reader.readAsDataURL(file);
            });
        }
        else {
            return Promise.resolve(URL.createObjectURL(file));
        }
    }
    createResizeHandle(pos, img) {
        const handle = createElement('div', {
            className: `e-image-rsz-handle e-resize-${pos}`
        });
        Object.assign(handle.style, {
            position: 'absolute',
            width: `${RESIZE_HANDLE_SIZE}px`,
            height: `${RESIZE_HANDLE_SIZE}px`,
            backgroundColor: '#0078d4',
            borderRadius: '50%',
            zIndex: '100',
            cursor: ['nw', 'se'].indexOf(pos) !== -1 ? 'nwse-resize' : 'nesw-resize',
            display: 'none'
        });
        handle.addEventListener('mousedown', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.startImageResize(e, img, handle);
        });
        return handle;
    }
    /**
     * Handles image resize
     *
     * @param {HTMLElement} container - The wrapper element that hosts the image and resize handles.
     * @param {HTMLImageElement} img - The image element to which resize handles are attached.
     * @returns {void}
     * @hidden
     */
    addResizeHandles(container, img) {
        const existingHandles = container.querySelectorAll('.e-image-rsz-handle');
        existingHandles.forEach((handle) => container.removeChild(handle));
        const positions = ['nw', 'ne', 'se', 'sw'];
        for (const pos of positions) {
            const handle = this.createResizeHandle(pos, img);
            container.appendChild(handle);
        }
        if (img.clientWidth > 0 || img.clientHeight > 0) {
            updateCSSText(container, `width: ${formatUnit(img.clientWidth)};`);
        }
    }
    startImageResize(e, img, handle) {
        this.isResizing = true;
        this.currentResizeHandle = handle;
        this.currentImage = img;
        this.startDimensions = { width: img.offsetWidth, height: img.offsetHeight };
        this.startPosition = { x: e.clientX, y: e.clientY };
        this.resizeOverlay = createElement('div', { id: this.parent.rootEditorElement.id + 'resize_overlay' });
        Object.assign(this.resizeOverlay.style, {
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            zIndex: '9999',
            cursor: handle.style.cursor
        });
        document.body.appendChild(this.resizeOverlay);
        document.addEventListener('mousemove', this.handleImageResize);
        document.addEventListener('mouseup', this.stopImageResize);
    }
    calculateNewDimensions(dx, dy, position) {
        let newWidth = this.startDimensions.width;
        let newHeight = this.startDimensions.height;
        switch (position) {
            case 'se':
                newWidth += dx;
                newHeight += dy;
                break;
            case 'sw':
                newWidth -= dx;
                newHeight += dy;
                break;
            case 'ne':
                newWidth += dx;
                newHeight -= dy;
                break;
            case 'nw':
                newWidth -= dx;
                newHeight -= dy;
                break;
        }
        newWidth = Math.max(newWidth, MIN_IMAGE_WIDTH);
        newHeight = Math.max(newHeight, MIN_IMAGE_HEIGHT);
        if (this.aspectRatio) {
            const widthChange = Math.abs(newWidth - this.startDimensions.width);
            const heightChange = Math.abs(newHeight - this.startDimensions.height);
            if (widthChange >= heightChange) {
                newHeight = newWidth / this.aspectRatio;
            }
            else {
                newWidth = newHeight * this.aspectRatio;
            }
        }
        return { newWidth, newHeight };
    }
    destroy() {
        this.removeEventListeners();
        // Unsubscribe from image events
        this.unsubscribeFromImageEvents();
        // Destroy upload popup
        if (this.uploadPopupObj) {
            this.uploadPopupObj.destroy();
            this.uploadPopupObj = null;
        }
        // Remove upload popup element
        if (this.uploadPopupElement && this.uploadPopupElement.parentElement) {
            detach(this.uploadPopupElement);
            this.uploadPopupElement = null;
        }
        // Clean up resize overlay
        if (this.resizeOverlay && this.resizeOverlay.parentNode) {
            document.body.removeChild(this.resizeOverlay);
        }
        // Clear references
        this.currentPlaceholder = null;
        this.isUploadPopupOpen = false;
    }
}
